import React, { useState } from 'react';
import { MdEmail, MdLock, MdPerson, MdPhone, MdBusiness, MdVpnKey, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import API from '../api';
import { LanguageSelector, useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';

// Self-service sign-up. Two modes: create a brand-new organization (its own
// separate system), or join an existing organization with its system code +
// staff join code. Nothing is created until the emailed link is opened.
// A staff invite link looks like /signup?org=acme-co&code=ABCD2345 and
// pre-fills the "Join my team" tab.
function Field({ icon, children }) {
    return (
        <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 18, display: 'flex' }}>{icon}</span>
            {children}
        </div>
    );
}

export default function SignUp() {
    const { t, language } = useLanguage();
    const { logoUrl } = useSiteSettings();
    const params = new URLSearchParams(window.location.search);
    const [mode, setMode] = useState(params.get('org') || params.get('code') ? 'join' : 'organization');
    const [form, setForm] = useState({
        name: '', email: '', phone: '', password: '', confirm: '',
        org_name: '', org_code: params.get('org') || '', join_code: params.get('code') || ''
    });
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sentTo, setSentTo] = useState('');
    const [notice, setNotice] = useState('');

    const set = key => e => setForm({ ...form, [key]: e.target.value });
    const inputStyle = { minHeight: 42, paddingLeft: 38 };
    const labelStyle = { fontWeight: 600, fontSize: 13, marginBottom: 6 };

    const handleSubmit = async e => {
        e.preventDefault();
        setError('');
        if (form.password.length < 12) { setError(t('signup.passwordTooShort')); return; }
        if (form.password !== form.confirm) { setError(t('signup.passwordsMismatch')); return; }
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
                password: form.password, language
            };
            if (mode === 'organization') {
                await API.post('/signup/organization', { ...payload, org_name: form.org_name.trim() });
            } else {
                await API.post('/signup/join', { ...payload, org_code: form.org_code.trim(), join_code: form.join_code.trim() });
            }
            setSentTo(form.email.trim());
        } catch (err) {
            setError(err.response?.data?.error || t('signup.failed'));
        } finally { setLoading(false); }
    };

    const resend = async () => {
        setNotice('');
        try { await API.post('/signup/resend', { email: sentTo }); } catch (err) { /* same message either way */ }
        setNotice(t('signup.resent'));
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #edf5ff 0%, #dfeeff 30%, #b8d6ff 60%, #0b5ed7 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px'
        }}>
            <div style={{ width: '100%', maxWidth: 440 }}>
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                    <img src={logoUrl || '/miti-logo.png'} alt="MITIFM logo" style={{ width: 56, height: 56, borderRadius: 14, objectFit: 'contain', marginBottom: 10, background: '#fff', padding: 4, boxShadow: '0 10px 24px rgba(11, 94, 215, 0.2)' }} />
                    <h1 style={{ color: '#0d1b2a', fontWeight: 800, fontSize: 26, margin: 0 }}>MITIFM</h1>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.97)', borderRadius: 14, padding: '26px 24px 22px', boxShadow: '0 22px 48px rgba(9, 32, 64, 0.18)', border: '1px solid rgba(11,94,215,0.08)' }}>
                    {sentTo ? (
                        <div style={{ textAlign: 'center' }}>
                            <MdEmail size={44} style={{ color: '#0b5ed7', marginBottom: 8 }} />
                            <h2 style={{ fontWeight: 750, fontSize: 20, color: '#0d1b2a' }}>{t('signup.checkEmailTitle')}</h2>
                            <p style={{ color: '#52657d', fontSize: 14 }}>{t('signup.checkEmailBody', '', { email: sentTo })}</p>
                            {notice && <div className="alert alert-success py-2" style={{ fontSize: 13 }}>{notice}</div>}
                            <button type="button" className="btn btn-outline-primary w-100 mb-2" onClick={resend}>{t('signup.resend')}</button>
                            <a href="/login" style={{ fontSize: 13, color: '#0b5ed7', fontWeight: 600 }}>{t('signup.signIn')}</a>
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 12, marginBottom: 5 }}>
                                <h2 style={{ flex: 1, fontWeight: 750, fontSize: 19, lineHeight: 1.25, margin: 0, color: '#0d1b2a' }}>{t('signup.title')}</h2>
                                <LanguageSelector style={{ flexShrink: 0 }} />
                            </div>
                            <p style={{ color: '#52657d', fontSize: 13, margin: '0 0 16px' }}>{t('signup.subtitle')}</p>

                            <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: '#eef3fa', padding: 4, borderRadius: 10 }}>
                                {[['organization', t('signup.tabOrg')], ['join', t('signup.tabJoin')]].map(([key, label]) => (
                                    <button key={key} type="button" onClick={() => { setMode(key); setError(''); }}
                                        style={{
                                            flex: 1, border: 'none', borderRadius: 8, padding: '8px 6px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                            background: mode === key ? '#fff' : 'transparent', color: mode === key ? '#0b5ed7' : '#52657d',
                                            boxShadow: mode === key ? '0 1px 4px rgba(9,32,64,.15)' : 'none'
                                        }}>{label}</button>
                                ))}
                            </div>

                            {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

                            <form onSubmit={handleSubmit}>
                                {mode === 'organization' ? (
                                    <div className="mb-3">
                                        <label className="form-label" style={labelStyle}>{t('signup.orgName')}</label>
                                        <Field icon={<MdBusiness />}>
                                            <input className="form-control" style={inputStyle} value={form.org_name} onChange={set('org_name')} maxLength={100} required autoComplete="organization" />
                                        </Field>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label" style={labelStyle}>{t('signup.orgCode')}</label>
                                            <Field icon={<MdBusiness />}>
                                                <input className="form-control" style={inputStyle} value={form.org_code} onChange={set('org_code')} autoCapitalize="none" autoComplete="off" placeholder="acme-co" required />
                                            </Field>
                                        </div>
                                        <div className="mb-1">
                                            <label className="form-label" style={labelStyle}>{t('signup.joinCode')}</label>
                                            <Field icon={<MdVpnKey />}>
                                                <input className="form-control" style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: 1 }} value={form.join_code} onChange={set('join_code')} autoComplete="off" required />
                                            </Field>
                                        </div>
                                        <div style={{ color: '#94a3b8', fontSize: 11.5, marginBottom: 14 }}>{t('signup.joinHint')}</div>
                                    </>
                                )}

                                <div className="mb-3">
                                    <label className="form-label" style={labelStyle}>{t('signup.name')}</label>
                                    <Field icon={<MdPerson />}>
                                        <input className="form-control" style={inputStyle} value={form.name} onChange={set('name')} maxLength={100} required autoComplete="name" />
                                    </Field>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label" style={labelStyle}>{t('signup.email')}</label>
                                    <Field icon={<MdEmail />}>
                                        <input type="email" className="form-control" style={inputStyle} value={form.email} onChange={set('email')} maxLength={150} required autoComplete="email" />
                                    </Field>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label" style={labelStyle}>{t('signup.phone')}</label>
                                    <Field icon={<MdPhone />}>
                                        <input type="tel" className="form-control" style={inputStyle} value={form.phone} onChange={set('phone')} maxLength={20} autoComplete="tel" />
                                    </Field>
                                </div>
                                <div className="mb-1">
                                    <label className="form-label" style={labelStyle}>{t('signup.password')}</label>
                                    <Field icon={<MdLock />}>
                                        <input type={showPass ? 'text' : 'password'} className="form-control" style={{ ...inputStyle, paddingRight: 42 }} value={form.password} onChange={set('password')} minLength={12} maxLength={128} required autoComplete="new-password" />
                                        <button type="button" onClick={() => setShowPass(!showPass)} aria-label={showPass ? t('account.hidePassword') : t('account.showPassword')}
                                            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4 }}>
                                            {showPass ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
                                        </button>
                                    </Field>
                                </div>
                                <div style={{ color: '#94a3b8', fontSize: 11.5, marginBottom: 12 }}>{t('signup.passwordHint')}</div>
                                <div className="mb-3">
                                    <label className="form-label" style={labelStyle}>{t('signup.confirmPassword')}</label>
                                    <Field icon={<MdLock />}>
                                        <input type={showPass ? 'text' : 'password'} className="form-control" style={inputStyle} value={form.confirm} onChange={set('confirm')} maxLength={128} required autoComplete="new-password" />
                                    </Field>
                                </div>

                                <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 14px' }}>
                                    {t('signup.agreePrefix')} <a href="/terms-of-service" target="_blank" rel="noreferrer">{t('signup.terms')}</a> {t('signup.and')} <a href="/privacy-policy" target="_blank" rel="noreferrer">{t('signup.privacy')}</a>.
                                </p>

                                <button type="submit" className="btn btn-primary w-100" disabled={loading} style={{ minHeight: 44, padding: 10, fontWeight: 700, fontSize: 14 }}>
                                    {loading ? <><span className="spinner-border spinner-border-sm me-2" />{t('signup.submitting')}</> : t('signup.submit')}
                                </button>
                            </form>

                            <p style={{ textAlign: 'center', fontSize: 13, color: '#52657d', margin: '16px 0 0' }}>
                                {t('signup.haveAccount')} <a href="/login" style={{ color: '#0b5ed7', fontWeight: 700 }}>{t('signup.signIn')}</a>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
